<template>
  <ModalDialog header-text="Add New Contact" @close-modal="closeModal" open>
    <template v-slot:content>
      <form @submit.prevent="saveFormAsync">
        <div class="flex-container">
          <div class="flex__left">
            <div v-if="formErrors.length">
              <b>Please correct the following error(s):</b>
              <ul>
                <li v-for="error in formErrors" :key="error">{{ error }}</li>
              </ul>
            </div>
            <div class="form-row">
              <label for="firstName"> First Name </label>
              <input
                v-model="firstName"
                id="firstName"
                name="firstName"
                type="text"
                class="form-control"
              />
            </div>
            <div class="form-row">
              <label for="lastName"> Last Name </label>
              <input
                v-model="lastName"
                id="lastName"
                name="lastName"
                type="text"
                class="form-control"
              />
            </div>
            <div class="form-row">
              <label for="email"> Email </label>
              <input
                v-model="email"
                id="email"
                name="email"
                type="text"
                class="form-control"
              />
            </div>
            <div class="form-row">
              <label for="primaryPhone"> Primary Phone </label>
              <input
                v-model="phone"
                id="primaryPhone"
                name="primaryPhone"
                type="text"
                class="form-control"
              />
            </div>
            <div class="form-row">
              <label></label>
              <LoadingButton :loading="saving" themeColor="success"
                >Save Company Contact</LoadingButton
              >
            </div>
          </div>
        </div>
      </form>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  companyId: number;
}>();

const emit = defineEmits<{
  (e: "closeModal", contactCreated: boolean): void;
}>();

const email = ref<string>("");
const firstName = ref<string>("");
const lastName = ref<string>("");
const phone = ref<string>("");

const saving = ref<boolean>(false);

// form validation errors
const formErrors = ref<string[]>([]);

function validEmail(email: string) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

function validateForm() {
  formErrors.value.length = 0;
  console.debug(
    "contact-dialog: validateForm(): email validation returns " +
      validEmail(email.value)
  );

  if (!firstName.value || !firstName.value.trim()) {
    formErrors.value.push("First Name required.");
  }

  if (!lastName.value || !lastName.value.trim()) {
    formErrors.value.push("Last Name required.");
  }

  if (!email.value) {
    formErrors.value.push("Email required.");
  } else if (!validEmail(email.value)) {
    formErrors.value.push("Invalid Email address.");
  }

  if (!phone.value || !phone.value.trim()) {
    formErrors.value.push("Phone required.");
  }

  return formErrors.value.length == 0;
}

const saveErrorNotify = () => {
  notificationStore.notify({
    message: `Error happened while saving contact. Please try again later.`,
    type: NotificationTypeEnum.error,
    duration: 5000,
  });
};

async function saveFormAsync() {
  if (!validateForm()) return;

  saving.value = true;

  let contactId = 0;

  // Create new contact first
  await handleResponsePromiseAsync(
    api.post("AddOrModifyContact", {
      CompanyLogin: props.companyId.toString(),
      ContactId: 0,
      JobTitle: "",
      Title: "",
      FirstName: firstName.value,
      LastName: lastName.value,
      Voice: phone.value,
      Fax: "",
      Cell: "",
      Email: email.value,
      EnteredBy: main.userLogin,
      ModifiedBy: main.userLogin,
      OtherInfo: "",
    }),
    (data) => {
      contactId = data;
    },
    "NewContactModal.vue",
    "saveFormAsync",
    saveErrorNotify
  );

  if (contactId <= 0) {
    saveErrorNotify();
    saving.value = false;
    return;
  }

  // Then create company contact
  await handleResponsePromiseAsync(
    api.post("AddOrModifyCustomerContact", {
      CompanyLogin: props.companyId.toString(),
      GridId: "miniCrm",
      PlantId: -1,
      IsCompanyContact: true,
      CcOrPcId: 0,
      ContactId: contactId,
      IsPrimary: false,
      IsBilling: false,
      IsWeb: false,
      IsCopacker: false,
      IsOther: true,
      ModifiedBy: main.userLogin,
      InvoiceDeliveryMethod: "N/A",
      StatementDeliveryMethod: "N/A",
      ScheduleAEmail: false,
      ScheduleBEmail: false,
      EirEmail: false,
      CompanyTitle: "",
    }),
    () => {
      notificationStore.notify({
        message: `Contact was created successfully`,
        type: NotificationTypeEnum.success,
        duration: 5000,
      });
      emit("closeModal", true);
    },
    "NewContactModal.vue",
    "saveFormAsync",
    saveErrorNotify
  );

  saving.value = false;
}

function closeModal() {
  emit("closeModal", false);
}
</script>

<style scoped>
.form-row {
  display: flex;
  margin-bottom: 10px;
  align-items: center;
  justify-content: flex-end;
}

.form-row label {
  font-weight: 200;
  text-align: right;
  min-width: 125px;
  margin-right: 20px;
}

.btn {
  cursor: pointer;
}
</style>
